import type { Message } from '@dia/db';

/** How often Mensajes asks the island for anything new while it is open. */
export const POLL_MS = 30_000;

export type ThreadMessage = {
  id: string;
  clientId: string;
  from: 'guest' | 'staff' | 'system';
  original: string;
  originalLang: 'es' | 'en';
  es: string | null;
  en: string | null;
  createdAt: string;
  /** True while the message is on this phone and the island has not confirmed it. */
  pending: boolean;
};

export function fromRow(row: Message): ThreadMessage {
  return {
    id: row.id,
    clientId: row.client_id,
    from: row.author_kind,
    original: row.text_original,
    originalLang: row.lang_original,
    es: row.text_es,
    en: row.text_en,
    createdAt: row.created_at,
    pending: false,
  };
}

/**
 * What to show in the reader's language, and whether it is a translation.
 * The original is always kept, and a tap reveals it when they differ.
 */
export function shownText(
  message: Pick<ThreadMessage, 'original' | 'originalLang' | 'es' | 'en'>,
  lang: 'es' | 'en',
): { text: string; translated: boolean; original: string } {
  if (message.originalLang === lang) {
    return { text: message.original, translated: false, original: message.original };
  }
  const shadow = lang === 'es' ? message.es : message.en;
  if (shadow && shadow.trim()) {
    return { text: shadow, translated: true, original: message.original };
  }
  // No shadow yet: show what was written, honestly, rather than an empty bubble.
  return { text: message.original, translated: false, original: message.original };
}

export type QueuedMessage = {
  clientId: string;
  payload: Record<string, unknown>;
  createdAt: number;
};

/**
 * The thread as the guest sees it: what the island has, plus what is still in
 * the outbox on this phone. A message that landed is matched by client_id and
 * shown once, as sent, not twice.
 */
export function mergeThread(server: ThreadMessage[], queued: QueuedMessage[]): ThreadMessage[] {
  const known = new Set(server.map((m) => m.clientId));
  const local: ThreadMessage[] = queued
    .filter((q) => !known.has(q.clientId))
    .map((q) => ({
      id: q.clientId,
      clientId: q.clientId,
      from: 'guest' as const,
      original: String(q.payload.text_original ?? ''),
      originalLang: (q.payload.lang_original === 'es' ? 'es' : 'en') as 'es' | 'en',
      es: (q.payload.text_es as string | null | undefined) ?? null,
      en: (q.payload.text_en as string | null | undefined) ?? null,
      createdAt: new Date(q.createdAt).toISOString(),
      pending: true,
    }));

  return [...server, ...local].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}
